/**
 * Alerts-panel sort logic (REQ-UI-110).
 *
 * The pure sort for the Alerts list — the default order + the selection-driven
 * sort that resolves ``platform_name`` via the producer-name join. Applied
 * AFTER the active-only filter (:func:`alerts/alerts-store.useActiveAlerts`).
 * Lives in this non-component module (not in :file:`./alerts-panel.tsx`) so the
 * panel file exports only its component (fast-refresh purity) and the sort is
 * unit-testable in isolation — the same split as :file:`./alert-display`.
 *
 * Cross-references:
 *   - REQ-UI-110 (operator list sort) / REQ-UI-051 (non-mutating; coexists with
 *     id-keyed selection).
 *   - ADR-029 §5 (priority) / §6.2 (Alerts panel).
 *   - @/lib/list-sort (the engine + comparator kit);
 *     @/lib/use-producer-names (the join).
 */
import type { AlertRecord } from "@/alerts/alerts-store";
import type { ProducerNameIndex } from "@/lib/use-producer-names";
import {
  type ComparatorMap,
  compareByRank,
  compareIso,
  compareStringsCI,
  type SortSelection,
  sortBy,
} from "@/lib/list-sort";
import type { AlertListSort } from "@/auth/session-types";

/**
 * The Alerts panel's built-in default order: time (``received_at``) descending
 * (newest first) — REQ-UI-110. Used when the working state carries no
 * ``list_sort.alerts`` selection.
 */
export const ALERT_DEFAULT_SORT: SortSelection<AlertListSort["field"]> = {
  field: "time",
  direction: "desc",
};

/**
 * Sort the (already active-filtered) alert records by the operator's selection
 * (REQ-UI-110), resolving ``platform_name`` via the producer-name join.
 *
 *   - ``time`` — chronological on ``received_at`` (the default, ``desc`` =
 *     newest first).
 *   - ``priority`` — by the SAPIENT ``AlertPriority`` enum int (higher = higher
 *     priority); paired with ``desc`` the most-urgent alert is first.
 *   - ``alert_type`` — by the SAPIENT ``AlertType`` enum int. (Sorting by the
 *     enum int groups same-type alerts together; the row shows the
 *     :func:`alertTypeLabel` label.)
 *   - ``platform_name`` — case-insensitive on the joined source-platform display
 *     name (falls back to the raw ``producer_id`` when unresolved).
 *
 * Every alert field here is non-nullable (``received_at`` / ``priority`` /
 * ``alert_type`` are required; ``platform_name`` falls back to the id), so there
 * is no nulls-last partition — every row sorts. Non-mutating, so the sort never
 * disturbs the id-keyed selection or the keep-in-view anchor (REQ-UI-051).
 *
 * @param alerts - The active (undismissed) alerts (newest-first as stored).
 * @param names - The ``producer_id → display_name`` resolver.
 * @param selection - The active sort selection (panel default when none).
 * @returns A new, sorted array.
 */
export function sortAlertRows(
  alerts: readonly AlertRecord[],
  names: ProducerNameIndex,
  selection: SortSelection<AlertListSort["field"]>,
): AlertRecord[] {
  const comparators: ComparatorMap<AlertRecord, AlertListSort["field"]> = {
    time: (a, b) => compareIso(a.received_at, b.received_at),
    priority: compareByRank((a) => a.priority),
    alert_type: compareByRank((a) => a.alert_type),
    platform_name: (a, b) =>
      compareStringsCI(names.nameFor(a.producer_id), names.nameFor(b.producer_id)),
  };
  return sortBy(alerts, selection, comparators);
}
